import React, { useEffect, useMemo, useState } from 'react';
import { ArrowSquareOut, Briefcase, MapPin, MapTrifold, IdentificationBadge } from '@phosphor-icons/react';
import { useObdGoogleStreetViewActions } from '../ObdGoogleStreetViewProvider';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import opportunityInfoService from '../../../../../services/oneButtonServices/opportunityInfoService';
import type { OpportunitySalesPerson } from '../../../../../@types/oneButtonType';
import {
    getPrimaryContactRecord,
    opportunityHasSunlightPartner,
    resolveContactCreditVisualStatus,
    type ContactCreditVisualStatus,
} from '../contactCreditStatus';
import SalesPersonModal from '../SalesPersonModal';
import { TitleInfoModal } from '../TitleInfoModal';
import { DashboardProjectHeaderActionDeck } from './DashboardProjectHeaderActionDeck';
import { CreditStatusModal } from '../CreditStatusModal';

export type DashboardV3ProjectHeaderProps = {
    theme: 'dark' | 'light';
    opportunityName: string;
    opportunityId: string;
    stageStatus: string;
    siteAddress?: string | null;
    utilityCompany: string;
    loading?: boolean;
    /** V2 split dashboard: quick-action deck in lead panel. Off for V3 layout. */
    showActionDeck?: boolean;
    isTitleContactExist?: boolean | null;
    onAddTitleContactsClick?: () => void;
    onBlackKnightTitleCheckClick?: () => void;
};

/** Matches OpportunityDetailsCard primary contact name (Last, First). */
function primaryContactDisplayName(details: any, fallback?: string | null): string {
    const primary =
        details?.Customers?.find((c: any) => c?.IsPrimaryContact || c?.isPrimaryContact) ??
        details?.Customers?.[0];
    if (!primary) return fallback?.trim() || '—';
    const last = String(primary.LastName ?? primary.lastName ?? '').trim();
    const first = String(primary.FirstName ?? primary.firstName ?? '').trim();
    const commaForm = [last, first].filter(Boolean).join(', ');
    if (commaForm) return commaForm;
    return `${first} ${last}`.trim() || fallback?.trim() || '—';
}

function formatAddress(details: any, fallback?: string | null): string {
    const install = details?.InstallationAddress;
    if (install) {
        const street = install.Street ?? install.street ?? '';
        const city = install.City ?? install.city ?? '';
        const state = install.stateCode ?? install.State ?? install.state ?? '';
        const zip = install.ZipCode ?? install.zipCode ?? '';
        const cityState = [city, state].filter(Boolean).join(', ');
        const line = [street, cityState, zip].filter(Boolean).join(' ');
        if (line.trim()) return line.trim();
    }
    return fallback?.trim() || '—';
}

/** Split "street city, ST zip" for compact two-line address display. */
function customerInitials(name: string): string {
    const cleaned = name?.trim() || '';
    if (!cleaned || cleaned === '…' || cleaned === '—') return '?';
    if (cleaned.includes(',')) {
        const [last, first] = cleaned.split(',').map((p) => p.trim());
        return `${(last?.[0] ?? '').toUpperCase()}${(first?.[0] ?? '').toUpperCase()}`.trim() || '?';
    }
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function splitServiceAddressLine(line: string): { primary: string; secondary: string } {
    const trimmed = line?.trim() || '—';
    if (trimmed === '—') return { primary: '—', secondary: '' };
    const zipMatch = trimmed.match(/^(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (zipMatch) {
        return {
            primary: zipMatch[1].trim(),
            secondary: `${zipMatch[2]} ${zipMatch[3]}`,
        };
    }
    const comma = trimmed.indexOf(',');
    if (comma > 0) {
        return {
            primary: trimmed.slice(0, comma).trim(),
            secondary: trimmed.slice(comma + 1).trim(),
        };
    }
    return { primary: trimmed, secondary: '' };
}

export const DashboardV3ProjectHeader: React.FC<DashboardV3ProjectHeaderProps> = ({
    theme,
    opportunityName,
    opportunityId,
    siteAddress,
    utilityCompany,
    loading = false,
    showActionDeck = false,
    isTitleContactExist = null,
    onAddTitleContactsClick,
    onBlackKnightTitleCheckClick,
}) => {
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const titleInfo = useOneButtonStore((s) => s.titleInfo);
    const setDashboardContactsModalOpen = useOneButtonStore((s) => s.setDashboardContactsModalOpen);
    const [salesPersonModalOpen, setSalesPersonModalOpen] = useState(false);
    const [titleInfoModalOpen, setTitleInfoModalOpen] = useState(false);
    const { openFromRoot, loading: streetViewLoading } = useObdGoogleStreetViewActions();

    const highlight = opportunityDetails?.OpportunityHighlight ?? {};
    const ahj = String((highlight as { AHJ?: string }).AHJ ?? '').trim();
    const managingOffice = String((highlight as { Managing_Office?: string }).Managing_Office ?? '').trim();

    const salesPerson = opportunityDetails?.SalesPerson as OpportunitySalesPerson | undefined;
    const hasSalesPerson = useMemo(
        () =>
            !!salesPerson &&
            [salesPerson.Name, salesPerson.Phone, salesPerson.Email, salesPerson.Sales_Division, salesPerson.Team].some(
                (v) => v != null && String(v).trim() !== ''
            ),
        [salesPerson]
    );

    const salesPersonName =
        salesPerson?.Name?.trim() ||
        salesPerson?.name?.trim() ||
        sessionStorage.getItem('salesPersonName')?.trim() ||
        '—';

    const leadSource =
        opportunityDetails?.OpportunityDetails?.LeadSource ??
        opportunityDetails?.LeadSource ??
        opportunityDetails?.leadSource ??
        '—';

    const serviceAddress = formatAddress(opportunityDetails, siteAddress);
    const addressLines = splitServiceAddressLine(serviceAddress);
    const titlePending =
        titleInfo == null
            ? false
            : Array.isArray(titleInfo)
              ? titleInfo.length === 0
              : !titleInfo;

    const displayName = useMemo(() => {
        if (loading) return '…';
        const fromContact = primaryContactDisplayName(opportunityDetails, '');
        if (fromContact && fromContact !== '—') return fromContact;
        return opportunityName?.trim() || opportunityId || 'Opportunity';
    }, [loading, opportunityDetails, opportunityName, opportunityId]);

    const initials = useMemo(() => customerInitials(displayName), [displayName]);

    const primaryContactName = useMemo(() => {
        if (loading) return '…';
        return primaryContactDisplayName(opportunityDetails, '');
    }, [loading, opportunityDetails]);

    const hasPrimaryContact = primaryContactName.trim() !== '' && primaryContactName !== '—' && primaryContactName !== '…';

    const primaryContactRecord = useMemo(
        () => getPrimaryContactRecord(opportunityDetails),
        [opportunityDetails],
    );

    const isSunlightPartnerExist = useMemo(
        () => opportunityHasSunlightPartner(opportunityDetails),
        [opportunityDetails],
    );

    const [creditStatusModalOpen, setCreditStatusModalOpen] = useState(false);

    const [sunlightCreditStatus, setSunlightCreditStatus] = useState<unknown>(null);

    const [utilityCompanyName, setUtilityCompanyName] = useState<string | null>(null);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);

    useEffect(() => {
        const oppId = opportunityDetails?.Opportunity_Id ?? opportunityId;
        if (!oppId || !isSunlightPartnerExist) {
            setSunlightCreditStatus(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await opportunityInfoService.getSunlightCreditStatus(oppId);
                if (!cancelled) {
                    setSunlightCreditStatus(res?.length ? res[0] : null);
                }
            } catch {
                if (!cancelled) setSunlightCreditStatus(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [opportunityDetails?.Opportunity_Id, opportunityId, isSunlightPartnerExist]);

    useEffect(() => {
        setUtilityCompanyName(solarUtilityData?.SolarUtility?.UtilityCompanyName ?? null)
    }, [solarUtilityData])

    const primaryContactCreditStatus: ContactCreditVisualStatus = useMemo(
        () =>
            resolveContactCreditVisualStatus(primaryContactRecord, true, {
                sunlightCreditStatus,
                isSunlightPartnerExist,
            }),
        [primaryContactRecord, sunlightCreditStatus, isSunlightPartnerExist],
    );

    const primaryContactCreditLabel =
        primaryContactCreditStatus === 'passed'
            ? 'Credit passed'
            : primaryContactCreditStatus === 'declined'
              ? 'Credit declined'
              : primaryContactCreditStatus === 'pending'
                ? 'Credit pending'
                : null;

    const openSalesforceOpportunity = () => {
        const sf = opportunityId?.trim();
        if (!sf) return;
        const base = (import.meta.env.VITE_SALESFORCE_URL || '').trim();
        if (base) window.open(`${base}${sf}/view`, '_blank');
    };

    return (
        <>
            <header
                className={`obd-dashboard-v3-project-header obd-dashboard-v3-project-header--split obd-theme-${theme}`}
            >
                <div className="obd-project-header-split__body">
                <section
                    className="obd-project-header-section obd-project-header-section--lead"
                    aria-labelledby="obd-project-header-lead-label"
                >
                    <div className="obd-project-header-section__accent" aria-hidden />
                    <div className="obd-project-header-section__inner">
                        <div className="obd-project-header-lead-card">
                            <span className="obd-project-header-lead-avatar" aria-hidden>
                                {initials}
                            </span>
                            <div className="obd-project-header-lead-card__body">
                                <p
                                    className="obd-project-header-section__eyebrow"
                                    id="obd-project-header-lead-label"
                                >
                                    Customer
                                </p>
                                <h1 className="obd-dashboard-v3-project-header__title">{displayName}</h1>
                            </div>
                        </div>
                    </div>
                </section>

                <section
                    className="obd-project-header-section obd-project-header-section--site"
                    aria-labelledby="obd-project-header-site-label"
                >
                    <div className="obd-project-header-section__inner">
                        <div className="obd-project-header-site-head">
                            <p className="obd-project-header-section__eyebrow" id="obd-project-header-site-label">
                                Project &amp; site
                            </p>
                            <div
                                className="obd-project-header-site-ribbon"
                                role="group"
                                aria-label="Project location and actions"
                            >
                                <div className="obd-project-header-site-ribbon__main">
                                    <MapPin
                                        size={16}
                                        weight="duotone"
                                        className="obd-project-header-site-ribbon__pin"
                                        aria-hidden
                                    />
                                    <span className="obd-project-header-site-ribbon__address">
                                        <span className="obd-project-header-site-ribbon__line">
                                            {addressLines.primary}
                                        </span>
                                        {addressLines.secondary ? (
                                            <span className="obd-project-header-site-ribbon__sub">
                                                {addressLines.secondary}
                                            </span>
                                        ) : null}
                                    </span>
                                    {titlePending ? (
                                        <span className="obd-project-header-lead-meta__flag">Title pending</span>
                                    ) : null}
                                </div>
                                <div className="obd-project-header-site-ribbon__trail">
                                    <button
                                        type="button"
                                        className="obd-project-header-site-ribbon__street-view"
                                        onClick={() => void openFromRoot(opportunityDetails)}
                                        disabled={streetViewLoading}
                                        title="Open Interactive Street View"
                                    >
                                        <MapTrifold size={14} weight="fill" aria-hidden />
                                        <span>Street View</span>
                                    </button>
                                    {opportunityId ? (
                                        <button
                                            type="button"
                                            className="obd-project-header-site-ribbon__opp"
                                            onClick={openSalesforceOpportunity}
                                            title={`Open opportunity ${opportunityId} in Salesforce`}
                                        >
                                            <Briefcase size={14} weight="duotone" aria-hidden />
                                            <span className="obd-project-header-site-ribbon__opp-id">{opportunityId}</span>
                                            <ArrowSquareOut size={12} weight="bold" aria-hidden />
                                        </button>
                                    ) : null}
                                    {showActionDeck ? (
                                        <DashboardProjectHeaderActionDeck layout="ribbon" />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <ul
                            className="obd-project-header-facts obd-project-header-facts--columns obd-project-header-facts--tiles"
                            role="list"
                            aria-label="Site and team details"
                        >
                            <li
                                className={`obd-project-header-fact obd-project-header-fact--tile${
                                    primaryContactCreditStatus !== 'none'
                                        ? ` obd-project-header-fact--credit-${primaryContactCreditStatus}`
                                        : ''
                                }`}
                                role="listitem"
                            >
                                <span className="obd-project-header-fact__label">
                                    Primary contact
                                    {primaryContactCreditLabel ? (
                                        <button
                                            type="button"
                                            className={`obd-project-header-fact__credit-badge obd-project-header-fact__credit-badge--${primaryContactCreditStatus}`}
                                            onClick={() => setCreditStatusModalOpen(true)}
                                            style={{ cursor: 'pointer' }}
                                            title="View credit status"
                                        >
                                            {primaryContactCreditStatus === 'passed'
                                                ? 'Passed'
                                                : primaryContactCreditStatus === 'declined'
                                                  ? 'Declined'
                                                  : 'Pending'}
                                        </button>
                                    ) : null}
                                </span>
                                {hasPrimaryContact ? (
                                    <button
                                        type="button"
                                        className="obd-dashboard-v3-project-header__meta-link obd-project-header-fact__link"
                                        onClick={() => setDashboardContactsModalOpen(true)}
                                        aria-label={`Open contacts for ${primaryContactName}`}
                                    >
                                        {primaryContactName}
                                    </button>
                                ) : (
                                    <span className="obd-project-header-fact__value">{primaryContactName}</span>
                                )}
                            </li>
                            {leadSource && leadSource !== '—' ? (
                                <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                    <span className="obd-project-header-fact__label">Lead source</span>
                                    <span className="obd-project-header-fact__value">{leadSource}</span>
                                </li>
                            ) : null}
                            <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                <span className="obd-project-header-fact__label">Utility</span>
                                <span className="obd-project-header-fact__value">
                                    { (utilityCompanyName?.trim() ?? utilityCompany?.trim()) || '—'}
                                </span>
                            </li>
                            <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                <span className="obd-project-header-fact__label">AHJ</span>
                                <span className="obd-project-header-fact__value">{ahj || '—'}</span>
                            </li>
                            <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                <span className="obd-project-header-fact__label">Managing office</span>
                                <span className="obd-project-header-fact__value">{managingOffice || '—'}</span>
                            </li>
                            <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                <span className="obd-project-header-fact__label">Sales person</span>
                                {hasSalesPerson ? (
                                    <button
                                        type="button"
                                        className="obd-dashboard-v3-project-header__meta-link obd-project-header-fact__link"
                                        onClick={() => setSalesPersonModalOpen(true)}
                                        aria-label="View sales person details"
                                    >
                                        {salesPersonName}
                                    </button>
                                ) : (
                                    <span className="obd-project-header-fact__value">{salesPersonName}</span>
                                )}
                            </li>
                            <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem">
                                <span className="obd-project-header-fact__label">Title</span>
                                <button
                                    type="button"
                                    className="obd-dashboard-v3-project-header__meta-link obd-project-header-fact__link"
                                    onClick={() => setTitleInfoModalOpen(true)}
                                    aria-label="Open title information"
                                >
                                    View title info
                                </button>
                            </li>
                            {isTitleContactExist !== null ? (
                                <li className="obd-project-header-fact obd-project-header-fact--tile" role="listitem" style={{ padding: 0, alignItems: 'stretch' }}>
                                    <button
                                        type="button"
                                        aria-label="Add title contacts"
                                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '6px', height: '100%', width: '100%', background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', borderRadius: '9px' }}
                                        onClick={
                                            isTitleContactExist === true
                                                ? onAddTitleContactsClick
                                                : onBlackKnightTitleCheckClick
                                        }
                                    >
                                        <IdentificationBadge size={16} weight="bold" aria-hidden />
                                        <span>Add Title Contacts</span>
                                    </button>
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </section>
                </div>
            </header>
            <SalesPersonModal
                open={salesPersonModalOpen}
                onClose={() => setSalesPersonModalOpen(false)}
                salesPerson={salesPerson}
            />
            <TitleInfoModal
                open={titleInfoModalOpen}
                onClose={() => setTitleInfoModalOpen(false)}
                raw={titleInfo}
                theme={theme}
            />
            <CreditStatusModal 
                open={creditStatusModalOpen}
                onClose={() => setCreditStatusModalOpen(false)}
                contact={primaryContactRecord}
                primaryFinancePartner={opportunityDetails?.OpportunityProduct?.[0]?.Finance_Partner || opportunityDetails?.opportunityProduct?.[0]?.Finance_Partner || ''}
            />
        </>
    );
};

export { triggerV3AiDesignsPanel } from './dashboardV3AiDesignsTrigger';
